import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CERTS } from '../data/content';
import { X, ChevronLeft, ChevronRight, Award, FileCheck } from 'lucide-react';

const SECTIONS = [
  { id: 'completion', label: 'Course Completion', sub: 'Dual, co-branded program certification.' },
  { id: 'endToEnd', label: 'End-to-End Industry Projects', sub: 'Complete real-world business projects, powered by Intellectra AI.' },
  { id: 'modules', label: 'Module-Based Project Certificates', sub: 'A real project certificate for every tool you master.' },
];

export default function Certifications() {
  const [lb, setLb] = useState({ open: false, group: null, index: 0 });

  const openLb = (group, index) => setLb({ open: true, group, index });
  const closeLb = () => setLb({ ...lb, open: false });
  const next = () => {
    const arr = CERTS[lb.group];
    setLb({ ...lb, index: (lb.index + 1) % arr.length });
  };
  const prev = () => {
    const arr = CERTS[lb.group];
    setLb({ ...lb, index: (lb.index - 1 + arr.length) % arr.length });
  };

  return (
    <section id="certificates" className="py-24 md:py-32">
      <div className="container-x">
        <div className="max-w-3xl">
          <div className="section-label">{CERTS.eyebrow}</div>
          <h2 className="font-display text-[42px] md:text-[56px] leading-[1.02] mt-5">
            {CERTS.title}
            <span className="grad-text-red">{CERTS.titleAccent}</span>
          </h2>
          <p className="mt-6 text-[16px] text-[color:var(--muted)] leading-relaxed">{CERTS.intro}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {['ExcellenC', 'Texas Tech University', 'Intellectra AI'].map((b) => (
              <span
                key={b}
                className="px-3 py-1.5 text-[11px] uppercase tracking-widest font-semibold rounded-full border border-line text-[color:var(--muted)]"
              >
                {b}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-16 space-y-16">
          {SECTIONS.map((s) => {
            const list = CERTS[s.id] || [];
            return (
              <div key={s.id}>
                <div className="flex items-baseline gap-4 border-b border-line pb-4">
                  <div className="font-display text-[32px] md:text-[40px] text-[color:var(--ink)] leading-none">
                    {list.length}
                  </div>
                  <div>
                    <div className="font-display text-[22px] md:text-[26px] leading-tight">{s.label}</div>
                    <div className="text-[13.5px] text-[color:var(--muted)] mt-1">{s.sub}</div>
                  </div>
                </div>

                <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {list.map((c, i) => (
                    <motion.button
                      key={c.title + i}
                      onClick={() => openLb(s.id, i)}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-40px' }}
                      transition={{ duration: 0.5, delay: i * 0.05 }}
                      className="card-lift text-left bg-white border border-line rounded-2xl overflow-hidden group"
                    >
                      <div className="aspect-[4/3] bg-[color:var(--bg-soft)] overflow-hidden">
                        <img
                          src={c.url}
                          alt={c.title}
                          className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        {c.tool && (
                          <div className="text-[10.5px] uppercase tracking-[0.2em] font-semibold text-red">{c.tool}</div>
                        )}
                        <div className="mt-1 font-display text-[17px] leading-snug text-[color:var(--ink)]">{c.title}</div>
                        <div className="mt-2 text-[13px] text-[color:var(--muted)]">{c.issuer}</div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Internship letters */}
        <div className="mt-16 border border-line rounded-3xl p-8 md:p-10 bg-white">
          <div className="flex items-center gap-3 mb-6">
            <Award size={20} className="text-red" />
            <div className="font-display text-[22px]">Internship Letters — Included</div>
          </div>
          <p className="text-[15px] text-[color:var(--muted)] max-w-2xl">
            Beyond certificates, your enrollment comes with official internship documentation for the entire program duration.
          </p>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {CERTS.internship.map((l) => (
              <div key={l.title} className="border border-line rounded-2xl p-6 bg-[color:var(--bg)]">
                <FileCheck size={22} className="text-navy" />
                <div className="mt-4 font-display text-[18px]">{l.title}</div>
                <div className="mt-2 text-[13px] text-[color:var(--muted)]">{l.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {lb.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={closeLb}
          >
            <button className="absolute top-6 right-6 text-white/80 hover:text-white" onClick={closeLb}>
              <X size={30} />
            </button>
            <button
              className="absolute left-6 md:left-10 text-white/60 hover:text-white"
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
            >
              <ChevronLeft size={40} />
            </button>
            <button
              className="absolute right-6 md:right-10 text-white/60 hover:text-white"
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
            >
              <ChevronRight size={40} />
            </button>
            <motion.img
              key={lb.index}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.25 }}
              src={CERTS[lb.group][lb.index].url}
              alt=""
              className="max-w-[92vw] max-h-[85vh] rounded-xl shadow-2xl bg-white"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
